import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { mfaGuard } from './core/guards/mfa.guard';
import { tenantSubdomainGuard } from './core/guards/tenant-subdomain.guard';
import { permissionGuard } from './core/guards/permission.guard';
import { nonProductionGuard } from './core/guards/non-production.guard';
import { appHomeGuard } from './core/guards/app-home.guard';
import { actAsTenantGuard } from './core/guards/act-as-tenant.guard';

export const APP_ROUTES: Routes = [
  // Página pública de marketing — no requiere autenticación
  {
    path: 'home',
    loadComponent: () => import('./features/home/home.component').then((m) => m.HomeComponent),
  },
  {
    path: '',
    pathMatch: 'full',
    loadComponent: () =>
      import('./features/landing/landing.component').then((m) => m.LandingComponent),
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./features/auth/login/login.component').then((m) => m.LoginComponent),
  },
  {
    path: 'mfa',
    loadComponent: () =>
      import('./features/auth/mfa-challenge/mfa-challenge.component').then(
        (m) => m.MfaChallengeComponent,
      ),
  },
  {
    path: 'login/mfa-setup',
    loadComponent: () =>
      import('./features/auth/mfa-setup/mfa-setup.component').then((m) => m.MfaSetupComponent),
  },
  {
    path: 'setup-password',
    loadComponent: () =>
      import('./features/auth/password-setup/password-setup.component').then(
        (m) => m.PasswordSetupComponent,
      ),
  },
  {
    path: 'auth/callback',
    loadComponent: () =>
      import('./features/auth/sso-callback/sso-callback.component').then(
        (m) => m.SsoCallbackComponent,
      ),
  },
  {
    // FGA-90: cambio forzado al primer login (llega vía `password_change_required`).
    path: 'auth/change-password',
    loadComponent: () =>
      import('./features/auth/change-password/change-password.component').then(
        (m) => m.ChangePasswordComponent,
      ),
  },
  {
    path: 'app',
    // tenantSubdomainGuard va DESPUÉS de authGuard: necesita `me` cargado, que es
    // lo que garantiza una sesión válida.
    canActivate: [authGuard, mfaGuard, tenantSubdomainGuard],
    loadComponent: () => import('./layout/app.layout.component').then((m) => m.AppLayoutComponent),
    children: [
      // Landing por rol (STATION_BASIC → station-portal; resto → tracking).
      { path: '', pathMatch: 'full', canActivate: [appHomeGuard], children: [] },
      {
        // Destino de `actAsTenantGuard`. Sin guard propio: es justamente la
        // salida para quien todavía no eligió tenant.
        path: 'select-tenant',
        loadComponent: () =>
          import('./features/select-tenant/select-tenant.component').then(
            (m) => m.SelectTenantComponent,
          ),
      },
      {
        // Sin guard propio: el catálogo tiene permisos separados por hijo
        // (`back-office.tenants.read` para tenants, `credit.topups.read` para
        // credit-topups) — un guard único acá le negaría el módulo entero a quien
        // sólo tenga uno de los dos. Igual que `fleet` más abajo.
        path: 'back-office',
        loadChildren: () =>
          import('./features/back-office/back-office.routes').then((m) => m.BACK_OFFICE_ROUTES),
      },
      {
        path: 'stations',
        canActivate: [permissionGuard('stations.read')],
        loadChildren: () =>
          import('./features/stations/stations.routes').then((m) => m.STATIONS_ROUTES),
      },
      {
        // /v1/vehicles, /v1/drivers, /v1/devices, /v1/credit-reservations: todos act-as.
        path: 'fleet',
        canActivate: [actAsTenantGuard],
        loadChildren: () => import('./features/fleet/fleet.routes').then((m) => m.FLEET_ROUTES),
      },
      {
        path: 'tracking',
        canActivate: [permissionGuard('tracking.read'), actAsTenantGuard],
        loadChildren: () =>
          import('./features/tracking/tracking.routes').then((m) => m.TRACKING_ROUTES),
      },
      {
        path: 'geofences',
        canActivate: [permissionGuard('geofences.read'), actAsTenantGuard],
        loadChildren: () =>
          import('./features/geofences/geofences.routes').then((m) => m.GEOFENCES_ROUTES),
      },
      {
        path: 'credit-rules',
        canActivate: [permissionGuard('credit.rules.read'), actAsTenantGuard],
        loadChildren: () =>
          import('./features/credit-rules/credit-rules.routes').then((m) => m.CREDIT_RULES_ROUTES),
      },
      {
        // Ojo: NO alcanza a back-office/credit-topups, que manda act-as por
        // request (tenant del formulario) y por eso no necesita el global.
        path: 'credit-balance',
        canActivate: [permissionGuard('credit.balance.read'), actAsTenantGuard],
        loadChildren: () =>
          import('./features/credit-balance/credit-balance.routes').then(
            (m) => m.CREDIT_BALANCE_ROUTES,
          ),
      },
      {
        path: 'transactions',
        canActivate: [permissionGuard('transactions.read')],
        loadChildren: () =>
          import('./features/transactions/transactions.routes').then((m) => m.TRANSACTIONS_ROUTES),
      },
      {
        path: 'alerts',
        canActivate: [permissionGuard('alerts.read')],
        loadChildren: () => import('./features/alerts/alerts.routes').then((m) => m.ALERTS_ROUTES),
      },
      {
        path: 'reports',
        canActivate: [permissionGuard('reports.read')],
        loadChildren: () =>
          import('./features/reports/reports.routes').then((m) => m.REPORTS_ROUTES),
      },
      {
        // /v1/shifts exige act-as (además de `stationId` en el body).
        path: 'station-portal',
        canActivate: [permissionGuard('dispatch.shifts.read'), actAsTenantGuard],
        loadChildren: () =>
          import('./features/station-portal/station-portal.routes').then(
            (m) => m.STATION_PORTAL_ROUTES,
          ),
      },
      {
        path: 'fiscal',
        canActivate: [permissionGuard('fiscal.read')],
        loadChildren: () => import('./features/fiscal/fiscal.routes').then((m) => m.FISCAL_ROUTES),
      },
      {
        path: 'audit',
        canActivate: [permissionGuard('audit.read')],
        loadChildren: () => import('./features/audit/audit.routes').then((m) => m.AUDIT_ROUTES),
      },
      {
        // M20 fase 1 — catálogo global/por país; SUPER_ADMIN opera SIN act-as.
        path: 'legal-documents',
        canActivate: [permissionGuard('legal-documents.read')],
        loadChildren: () =>
          import('./features/legal-documents/legal-documents.routes').then(
            (m) => m.LEGAL_DOCUMENTS_ROUTES,
          ),
      },
      {
        path: 'bulk-import',
        canActivate: [permissionGuard('bulk-import.read')],
        loadChildren: () =>
          import('./features/bulk-import/bulk-import.routes').then((m) => m.BULK_IMPORT_ROUTES),
      },
      {
        path: 'privacy',
        canActivate: [permissionGuard('privacy.read')],
        loadChildren: () =>
          import('./features/privacy/privacy.routes').then((m) => m.PRIVACY_ROUTES),
      },
      {
        // 🧪 Demo dev-only de cancelación de despacho (FGA-19). Quitar cuando exista
        // el monitor real de despacho (Sprint 4).
        path: 'dispatch-demo',
        canActivate: [nonProductionGuard],
        loadComponent: () =>
          import('./features/back-office/dispatch-monitor/dispatch-demo/dispatch-demo.component').then(
            (m) => m.DispatchDemoComponent,
          ),
      },
      {
        path: 'settings',
        canActivate: [permissionGuard('settings.read')],
        loadChildren: () =>
          import('./features/settings/settings.routes').then((m) => m.SETTINGS_ROUTES),
      },
    ],
  },
  {
    // Simulador de ingesta telemétrica — dev-only, fuera de /app (sin authGuard/mfaGuard).
    path: 'telemetry-sim',
    canActivate: [nonProductionGuard],
    loadChildren: () =>
      import('./features/telemetry/telemetry.routes').then((m) => m.TELEMETRY_ROUTES),
  },
  {
    path: 'account-suspended',
    loadComponent: () =>
      import('./features/shared/account-suspended.component').then(
        (m) => m.AccountSuspendedComponent,
      ),
  },
  {
    path: 'not-authorized',
    loadComponent: () =>
      import('./features/shared/not-authorized.component').then((m) => m.NotAuthorizedComponent),
  },
  {
    path: 'navigation-unavailable',
    loadComponent: () =>
      import('./features/shared/navigation-unavailable.component').then(
        (m) => m.NavigationUnavailableComponent,
      ),
  },
  {
    path: 'not-found',
    loadComponent: () =>
      import('./features/shared/not-found.component').then((m) => m.NotFoundComponent),
  },
  { path: '**', redirectTo: 'not-found' },
];
